/* eslint-disable */

import React, { useState, useCallback, useContext, createContext } from 'react';
import { storage, resetStorages } from 'utils/appLocalStorage';
import { useNewToast } from 'hooks/newToast';
import { appWarnings } from 'utils/appWarnings';
import api from '../services';
import {
  getAccessToken,
  getRefreshToken,
  persistSessionTokens,
  setOnSessionInvalid,
} from '../services/sessionRefresh';

const errorMessage = {
  type: 'error',
  message: 'Usuário ou senha inválidos, verifique os seus dados!',
  autoClose: false,
};

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const { showToast, dismiss } = useNewToast();

  const [loading, setLoading] = useState();
  const [errorLogin, setErrorLogin] = useState(true);
  const [isAuth, setIsAuth] = useState(false);
  const [forgotLoading, setForgotLoading] = React.useState(false);
  const [resetLoading, setResetLoading] = React.useState(false);
  const [isOldUser, setIsOldUser] = React.useState(false);
  const [updateLoading, setUpdateLoading] = React.useState(false);
  const [statusTutorial, setStatusTutorial] = React.useState(true);
  const [plano, setPlano] = React.useState('basico');

  const [ultimaFatura, setUltimaFatura] = React.useState({});
  const [ultimaFaturaLoading, setUltimaFaturaLoading] = React.useState({});

  const [data, setData] = useState({
    token: 'initial',
    user: undefined,
  });

  const [isLoginModalVisible, setLoginModalVisible] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  const showLoginModal = () => setLoginModalVisible(true);
  const hideLoginModal = () => setLoginModalVisible(false);

  const signIn = useCallback(
    async ({ login, password }) => {
      try {
        setLoading(true);
        const response = await api
          .auth()
          .verificarCredenciais({ login, password, modo: 'web' });

        const { token, accessToken, refreshToken, user, tutorial } = response;
        const nextAccessToken = accessToken || token;

        if (!nextAccessToken || !refreshToken) {
          setLoading(false);
          return showToast(errorMessage);
        }

        setStatusTutorial(tutorial);

        setPlano(user.plano);

        persistSessionTokens({
          accessToken: nextAccessToken,
          refreshToken,
        });
        localStorage.setItem(storage.tutorial, tutorial);

        setIsAuth(true);
        setIsOldUser(false);

        setData({ token: nextAccessToken, user });
        dismiss();
        setLoading(false);

        return {
          ...response,
          token: nextAccessToken,
          accessToken: nextAccessToken,
          refreshToken,
        };
      } catch (err) {
        setIsOldUser(false);
        setLoading(false);
        setIsAuth(false);
        setErrorLogin(false);

        if (err?.response?.data?.type === 'financeiro') {
          showToast(appWarnings.Danger2(err));
          throw new Error();
        }

        if (err?.response?.data?.type === 'atualizar_cadastro') {
          setIsOldUser(true);
          setData({ token: null, user: { login, senha: password } });
          throw new Error();
        }
        showToast(appWarnings.authError(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  React.useEffect(() => {
    setOnSessionInvalid(
      ({ financial, error, showLoginModal: shouldShowModal }) => {
        resetStorages();
        setData({ token: null, user: {} });
        setIsAuth(false);
        if (financial) {
          showToast(appWarnings.Danger2(error));
          return;
        }
        if (shouldShowModal !== false) {
          setLoginModalVisible(true);
        }
      },
    );
  }, [showToast]);

  React.useEffect(() => {
    const token = getAccessToken();

    if (token) {
      api
        .auth()
        .verificarToken()
        .then((response) => {
          setData({ token: getAccessToken(), user: response.user });
          setPlano(response.user.plano);
          setStatusTutorial(response.tutorial);
          setIsAuth(true);
          setAuthInitialized(true);
        })
        .catch((err) => {
          if (err?.refreshKind === 'network' || err?.refreshKind === 'server') {
            const preservedToken = getAccessToken();
            if (preservedToken) {
              setData((current) => ({
                token: preservedToken,
                user: current.user,
              }));
              setIsAuth(true);
            }
            setAuthInitialized(true);
            return;
          }

          resetStorages();
          setData({ token: null, user: {} });
          setIsAuth(false);
          setAuthInitialized(true);
          if (err?.response?.data?.type === 'financeiro') {
            showToast(appWarnings.Danger2(err));
          }
        });
    } else {
      setData({ token: null, user: {} });
      setIsAuth(false);
      setAuthInitialized(true);
    }
  }, []);

  const signOut = useCallback(() => {
    const refreshToken = getRefreshToken();
    const logoutRequest = refreshToken
      ? api
          .auth()
          .logoutSession({ refreshToken })
          .catch(() => {})
      : Promise.resolve();

    resetStorages();
    setData({});
    setIsAuth(false);

    return logoutRequest;
  }, []);

  const updateUser = useCallback(
    (user) => {
      localStorage.setItem(storage.user, JSON.stringify(user));

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token],
  );

  const forgotPassword = useCallback(async ({ email }) => {
    try {
      setForgotLoading(true);
      await api.auth().forgotPassword({ email });

      setForgotLoading(false);
    } catch (err) {
      setForgotLoading(false);
      showToast(appWarnings.Danger2(err));
      throw new Error(err);
    }
  }, []);

  const resetPassword = useCallback(async ({ senha, token }) => {
    try {
      setResetLoading(true);
      await api.auth().resetPassword({ senha, token });

      setResetLoading(false);
    } catch (err) {
      setResetLoading(false);
      showToast(appWarnings.Danger2(err));

      throw new Error(err);
    }
  }, []);

  const updateLogin = useCallback(async ({ login, senha, email }) => {
    try {
      setUpdateLoading(true);
      await api.auth().updateLogin({ login, senha, email });
      showToast({
        type: 'success',
        message: 'Login alterado com sucesso',
      });
      setIsOldUser(false);
      setUpdateLoading(false);
    } catch (err) {
      setUpdateLoading(false);
      showToast(appWarnings.Danger2(err));

      throw new Error(err);
    }
  }, []);

  const buscarUltimaFatura = useCallback(async () => {
    try {
      setUltimaFaturaLoading(true);
      const response = await api.auth().lastFatura();
      setUltimaFatura(response);
      setUltimaFaturaLoading(false);
    } catch (err) {
      setUltimaFaturaLoading(false);
      throw new Error(err);
    }
  }, []);

  React.useEffect(() => {
    if (isAuth) {
      buscarUltimaFatura();
    } else {
      setUltimaFatura({});
    }
  }, [isAuth, buscarUltimaFatura]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        token: data.token,
        habilitarColetor: data.user?.habilitarColetor === true,
        plano,
        tutorial: statusTutorial,
        errorLogin,
        signIn,
        signOut,
        setStatusTutorial,
        updateUser,
        loading,
        isAuth,
        forgotPassword,
        forgotLoading,
        resetPassword,
        resetLoading,
        isOldUser,
        updateLogin,
        updateLoading,
        ultimaFatura,
        ultimaFaturaLoading,
        isLoginModalVisible,
        showLoginModal,
        hideLoginModal,
        authInitialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuth must be used within an AuthProvider');

  return context;
}
